import { Link, Navigate, useLocation } from 'react-router-dom';

import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import useAuthentication from '@/hooks/common/useAuthentication.ts';
import useFetch from '@/hooks/common/useFetch';
import useToast from '@/hooks/store/useToast';

import CSSModule from './EmailConfirmation.module.scss';

export type EmailConfirmationState = {
  email: string;
};

const EmailConfirmation: React.FC = () => {
  const { isLoggedIn } = useAuthentication();
  const location = useLocation();
  const email = (location.state as EmailConfirmationState | null)?.email;
  const { showToast } = useToast();

  const { fetch, isFetching, error } = useFetch<null>(
    'POST',
    '/api/school/parents/password_change_tokens/create_v2',
  );

  // 再送信には送信先のメールアドレスが必要なため、入力画面を経由していなければ戻す
  if (!email) {
    return <Navigate replace to="/forgot" />;
  }

  const onResend = async () => {
    const { status } = await fetch({ body: { email } });
    if (status === 201) {
      showToast(`${email} にメールを送信しました`);
    }
  };

  return (
    <div className={CSSModule.EmailConfirmation}>
      <h3 className={CSSModule.EmailConfirmation__Title}>
        送信されたメールを確認してください
      </h3>
      <p className={CSSModule.EmailConfirmation__Description}>
        <span className={CSSModule.EmailConfirmation__Email}>{email}</span>
        へメールを送信しました。
        <br />
        メールに記載されているURLから
        <br className={CSSModule.EmailConfirmation__SpBreak} />
        パスワードを再設定してください。
      </p>
      <p className={CSSModule.EmailConfirmation__NotReceivedTitle}>
        メールが届かない場合
      </p>
      <p className={CSSModule.EmailConfirmation__Remark}>
        ※ 上記メールアドレスに誤りがないかご確認ください。
      </p>
      <p className={CSSModule.EmailConfirmation__Remark}>
        ※ ドメイン指定受信設定で「@studysapuri.jp」の許可と
        <br />
        URL付きメールの受信許可設定をして
        <br className={CSSModule.EmailConfirmation__SpBreak} />
        メールを再送信してください。
      </p>
      <ErrorList
        errors={error ? [error.message] : null}
        className={CSSModule.EmailConfirmation__Error}
      />
      <div className={CSSModule.EmailConfirmation__Buttons}>
        <Button
          color="primary"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={onResend}
          disabled={isFetching}
          className={CSSModule.EmailConfirmation__Button}
        >
          メールを再送信
        </Button>
        <Button
          tag={Link}
          to={isLoggedIn ? '/mypage/profile' : '/login'}
          color="outline"
          className={CSSModule.EmailConfirmation__Button}
        >
          {isLoggedIn ? 'マイページに戻る' : 'ログイン画面に戻る'}
        </Button>
      </div>
    </div>
  );
};

export default EmailConfirmation;
